/**
 * S14's neighbourhood reading, as pure functions. Backlog task 82.
 *
 * `Fleet-Selection-Rule.md` §8 does not read the cohort as 168 rows. It reads it as **7 families ×
 * 28 swept dimensions**, each dimension a short ordered run of four parameter points off a shared
 * default, repeated on two timeframes — and the test it applies is adjacency:
 *
 * > *"If a family passes at three adjacent points on a dimension, that is a **neighbourhood** and
 * > the result is unlikely to be luck. If it passes at exactly one point and fails both neighbours,
 * > that is noise wearing a winner's badge."*
 *
 * At twenty rows that reading can be done by eye. At 168 it cannot, which is why §8 lists *"S14 has
 * no family or neighbourhood view"* as a **precondition of the section being executable** rather
 * than as a nicety. This module is that reading; `FleetScreen` renders what it returns.
 *
 * ## 🔴 The swept dimension is not stored, and that is deliberate
 *
 * `GET /api/v1/fleet` returns each candidate's `parameters` and nothing that says which key was
 * swept to produce it. It does not need to: within one family and one timeframe, **exactly one
 * candidate holds the base configuration and every other differs from it on exactly one
 * dimension** — `tests/test_seed_fleet.py::test_a_swept_point_differs_from_the_base_on_exactly_one_dimension`
 * asserts precisely that, so the dimension is a diff against the family's own default and is
 * recoverable here.
 *
 * ⚠ **Recoverable, and this module must not pretend otherwise when it is not.** The base is found
 * structurally (see {@link baseOf}) and the answer can legitimately be *unknown* — a family swept
 * on one dimension only has four indistinguishable hubs. Where that happens the grouping still
 * holds and the default simply goes unmarked, because an arbitrary pick would put a `•` on a
 * candidate that is not the recipe's default and nothing downstream would ever contradict it.
 *
 * ## The one exception, mirrored from the seeder rather than invented
 *
 * `stop_pct` carries `risk_pct` with it — `seed_fleet.py`: *"a stop sweep would silently be a
 * position-size sweep as well"* — so a `stop_pct` point differs from its base on **two** keys. That
 * pairing is the single documented exception in the seeder and in its test, and {@link COUPLED}
 * restates it here. It is a copy of a fact rather than a second decision: if the seeder ever
 * couples another pair, this constant is where the screen finds out it was wrong.
 */

import type { FleetCandidate } from '../api/generated';

/**
 * Keys that move *with* another key and therefore do not make a second dimension.
 *
 * The seeder's own exception, and its test's: `allowed = {dimension} | ({"risk_pct"} if dimension
 * == "stop_pct" else set())`. Written as rider → dimension so a diff can collapse it in one lookup.
 */
export const COUPLED: Readonly<Record<string, string>> = { risk_pct: 'stop_pct' };

/** A pass, in §8's sense. The one significance state that counts toward a neighbourhood. */
export const PASS = 'promotable';

/** How a dimension's run of four reads under §8. */
export type Reading = 'neighbourhood' | 'pair' | 'isolated' | 'none';

/** One point on one swept dimension: a candidate, plus what §8 makes of its position in the run. */
export interface SweepPoint {
  candidate: FleetCandidate;
  /** The value this point holds on its dimension, as it arrived. */
  value: unknown;
  /** True when this candidate holds the family's base configuration — the `•`. */
  isDefault: boolean;
  /**
   * A pass with a passing neighbour. 🔴 **A pass without one is a non-result**, not a winner: §8
   * calls it *"noise wearing a winner's badge"* and §3 would otherwise promote it.
   */
  supported: boolean;
}

/** One dimension of one family on one timeframe — the four glyphs that are the whole §8 test. */
export interface DimensionRun {
  dimension: string;
  timeframe: string;
  points: SweepPoint[];
  reading: Reading;
}

/** One collapsed row of the default view, and everything its expansion needs. */
export interface FamilyGroup {
  family: string;
  candidates: number;
  /** Ascending by bar length, so `1h` sits left of `4h` and the pair reads as a pair. */
  timeframes: string[];
  /** First-seen order across the timeframes, so both columns list dimensions the same way. */
  dimensions: string[];
  runs: DimensionRun[];
  /** Runs reading `neighbourhood`. The number the morning read is actually looking for. */
  neighbourhoods: number;
  /** Passes with a passing neighbour. */
  supported: number;
  /** Passes without one. Counted separately because they are **not** results. */
  isolated: number;
  flagged: number;
  /** Best significance state present, per the spec's collapsed row. */
  best: string;
  /**
   * Candidates that are not a point on any dimension: the `buy_and_hold` control, anything from a
   * cohort that was not seeded as a sweep, and any row whose parameters place it on two dimensions
   * at once. ⚠ **Shown rather than dropped** — §8 says a candidate that is not a point on a
   * declared dimension is invisible to §3, and a screen that silently omitted it would hide the
   * seeding mistake that produced it.
   */
  unswept: FleetCandidate[];
}

/** Minutes per bar, for ordering the timeframe columns. Unknown units sort last. */
const TIMEFRAME_MINUTES: Readonly<Record<string, number>> = {
  m: 1,
  h: 60,
  d: 1440,
  w: 10080,
};

export function timeframeRank(timeframe: string): number {
  const match = /^(\d+)([mhdw])$/.exec(timeframe);
  const unit = match?.[2];
  if (unit === undefined) return Number.MAX_SAFE_INTEGER;
  return Number(match?.[1]) * (TIMEFRAME_MINUTES[unit] ?? Number.MAX_SAFE_INTEGER);
}

/** A parameter value as a number, or `null` when it is not one. Decimals arrive as strings (H6). */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Whether two parameter values are the same point.
 *
 * Numeric where both sides are numeric, so `8` and `"8"` and `"8.0"` are one value rather than
 * three. The seeder is consistent per key, but a comparison that depends on that consistency would
 * turn a JSON type change into a screen that silently stopped grouping.
 */
function samePoint(left: unknown, right: unknown): boolean {
  const a = asNumber(left);
  const b = asNumber(right);
  if (a !== null && b !== null) return a === b;
  return String(left) === String(right);
}

/**
 * The dimensions on which two parameter sets differ, with riders collapsed onto their dimension.
 *
 * A key present on one side and absent on the other counts as a difference — `undefined` is not a
 * value, and treating it as one would let a candidate missing a parameter look like the base.
 */
export function dimensionsBetween(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): string[] {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  const differing = new Set<string>();
  for (const key of keys) {
    const inLeft = Object.hasOwn(left, key);
    const inRight = Object.hasOwn(right, key);
    if (inLeft !== inRight || !samePoint(left[key], right[key])) {
      differing.add(COUPLED[key] ?? key);
    }
  }
  return [...differing];
}

/**
 * The family's base configuration within one timeframe, or `null` when it cannot be told.
 *
 * **Structural, not statistical.** The base is the *hub*: the candidate the most others sit one
 * dimension away from. In a clean rung of `1 + d × 3` that is decisive — the default is one
 * dimension from all `d × 3` variants, while a `fast_period` variant is one dimension from only the
 * default and its two siblings, because it differs from every `slow_period` variant on two.
 *
 * ⚠ **Argmax rather than "differs from *every* other by ≤ 1", which was the first version and was
 * too brittle to use.** One malformed row — a candidate that moved two dimensions at once, which
 * {@link FamilyGroup.unswept} exists to display — disqualifies the real default under that stricter
 * rule, and the whole family then falls out of the grouping. A single bad row must cost one row.
 *
 * 🔴 **`null` is a real answer and the caller must handle it.** A family swept on a single
 * dimension has four candidates all one dimension from each other, so they tie and none is
 * identifiable. The grouping is unaffected — there is only one dimension for them to be on — but
 * the default goes unmarked, which is honest. Picking the first, or the modal value, would print a
 * `•` that no data supports.
 */
export function baseOf(group: FleetCandidate[]): FleetCandidate | null {
  if (group.length === 0) return null;
  const adjacency = group.map((candidate) =>
    group.filter(
      (other) =>
        other !== candidate &&
        dimensionsBetween(candidate.parameters, other.parameters).length <= 1,
    ).length,
  );
  const best = Math.max(...adjacency);
  const hubs = group.filter((_c, i) => adjacency[i] === best);
  return hubs.length === 1 ? (hubs[0] ?? null) : null;
}

/**
 * Every key that takes more than one value across the group, riders collapsed **after** counting.
 *
 * 🔴 **The order of those two steps is the whole function, and having them the other way round was
 * a real defect.** Collapsing first put `stop_pct`'s `'8'` and `risk_pct`'s `'0.16'` into one
 * bucket, which then held two values — so `stop_pct` was reported as swept on a rung where neither
 * key varied at all. The consequence was not a cosmetic one: `assign`'s fallback only groups when
 * *exactly one* dimension is swept, so a rung genuinely swept on one dimension saw two, fell
 * through to `unswept`, and vanished from the grid while the screen announced *"4 candidates not on
 * a swept dimension"* about a correct sweep.
 *
 * ⚠ Variance is a property of a **key**; a dimension is what a key is *called* once its rider is
 * folded in. Counting distinct values per key and only then mapping the name keeps a `stop_pct`
 * sweep as one dimension — both keys move, both collapse — without letting two keys that each hold
 * one value pretend between them to hold two.
 */
function sweptKeys(group: FleetCandidate[]): string[] {
  const seen = new Map<string, unknown[]>();
  for (const candidate of group) {
    for (const [key, value] of Object.entries(candidate.parameters)) {
      const values = seen.get(key) ?? [];
      if (!values.some((known) => samePoint(known, value))) values.push(value);
      seen.set(key, values);
    }
  }
  const dimensions = new Set<string>();
  for (const [key, values] of seen) {
    if (values.length > 1) dimensions.add(COUPLED[key] ?? key);
  }
  return [...dimensions];
}

/**
 * Which dimension each candidate is a point on, within one (family, timeframe) group.
 *
 * The base — when there is one — is a point on **every** dimension, which is what makes each
 * dimension four points long off three variants: §8's `1 + d × (p − 1)`.
 */
function assign(group: FleetCandidate[]): {
  byDimension: Map<string, FleetCandidate[]>;
  base: FleetCandidate | null;
  unswept: FleetCandidate[];
} {
  const byDimension = new Map<string, FleetCandidate[]>();
  const unswept: FleetCandidate[] = [];
  const base = baseOf(group);

  if (base === null) {
    // No identifiable hub. Grouping is still sound in the one case that matters — a single swept
    // dimension, where every candidate is a point on it by construction. Anything else is not a
    // sweep this module can read, and says so rather than guessing.
    const swept = sweptKeys(group);
    const [only] = swept;
    if (only !== undefined && swept.length === 1) {
      byDimension.set(only, [...group]);
    } else {
      unswept.push(...group);
    }
    return { byDimension, base, unswept };
  }

  for (const candidate of group) {
    if (candidate === base) continue;
    const [dimension, ...also] = dimensionsBetween(base.parameters, candidate.parameters);
    if (dimension !== undefined && also.length === 0) {
      const points = byDimension.get(dimension) ?? [];
      points.push(candidate);
      byDimension.set(dimension, points);
    } else {
      // Two dimensions at once is not a sweep point. It cannot be read as a neighbourhood, so §8
      // makes it invisible to §3 — and this screen shows it for the same reason.
      unswept.push(candidate);
    }
  }
  if (byDimension.size === 0) {
    unswept.push(base);
  } else {
    for (const points of byDimension.values()) points.push(base);
  }
  return { byDimension, base, unswept };
}

/** Ascending by value: numeric where every point is numeric, lexical otherwise. */
function inParameterOrder(points: SweepPoint[]): SweepPoint[] {
  const allNumeric = points.every((point) => asNumber(point.value) !== null);
  return [...points].sort((a, b) =>
    allNumeric
      ? (asNumber(a.value) as number) - (asNumber(b.value) as number)
      : String(a.value).localeCompare(String(b.value)),
  );
}

/** Where the longest unbroken run of passes sits, and how long it is. Empty runs give length 0. */
export interface PassSpan {
  /** Index of the first pass in the longest run. `-1` when there is no pass at all. */
  start: number;
  length: number;
}

/**
 * The longest unbroken run of passes, as a position rather than a verdict.
 *
 * 🔴 **This exists so the screen can *draw* the neighbourhood instead of only naming it**, and it
 * lives here rather than in `FleetScreen` for one reason: {@link readingOf} is derived from it. A
 * bracket drawn over cells 2–4 beside a label reading *"isolated"* would be the worst kind of
 * defect on this screen — two elements disagreeing about the one test §8 turns on — and the only
 * way to make that unrepresentable is to give both the same source.
 */
export function longestPassSpan(states: string[]): PassSpan {
  const best: PassSpan = { start: -1, length: 0 };
  let from = 0;
  let run = 0;
  for (const [i, state] of states.entries()) {
    if (state === PASS) {
      if (run === 0) from = i;
      run += 1;
      if (run > best.length) {
        best.start = from;
        best.length = run;
      }
    } else {
      run = 0;
    }
  }
  return best;
}

/**
 * §8's test, applied to a run in parameter order.
 *
 * Three adjacent passes is a neighbourhood. One pass with no passing neighbour is the isolated
 * point §8 calls a non-result. Two adjacent is neither — it is more than noise and less than a
 * neighbourhood, and calling it either would be the screen making a judgement the rule does not.
 */
export function readingOf(states: string[]): Reading {
  const { length } = longestPassSpan(states);
  if (length >= 3) return 'neighbourhood';
  if (length === 2) return 'pair';
  return length === 1 ? 'isolated' : 'none';
}

/** Best significance present, most advanced first. `flagged` is a bug signal, never a "best". */
const BEST_ORDER = [PASS, 'too_early', 'not_ranked', 'flagged'];

function bestOf(candidates: FleetCandidate[]): string {
  for (const state of BEST_ORDER) {
    if (candidates.some((candidate) => candidate.significance === state)) return state;
  }
  return candidates[0]?.significance ?? 'not_ranked';
}

/**
 * The whole default view: one entry per family, in first-seen order.
 *
 * 🔴 **First-seen order, and never a global sort by a metric.** §8's reading depends on adjacency
 * inside a run, and the spec is explicit that *"a global sort destroys the adjacency the reading
 * depends on"*. Families keep the order the API returned them in — which is the seeder's own
 * declaration order — so the same family is in the same place every morning.
 */
export function groupFleet(candidates: FleetCandidate[]): FamilyGroup[] {
  const families = new Map<string, FleetCandidate[]>();
  for (const candidate of candidates) {
    const members = families.get(candidate.candidate) ?? [];
    members.push(candidate);
    families.set(candidate.candidate, members);
  }

  const groups: FamilyGroup[] = [];
  for (const [family, members] of families) {
    const timeframes = [...new Set(members.map((m) => m.timeframe))].sort(
      (a, b) => timeframeRank(a) - timeframeRank(b) || a.localeCompare(b),
    );
    const runs: DimensionRun[] = [];
    const dimensions: string[] = [];
    const unswept: FleetCandidate[] = [];

    for (const timeframe of timeframes) {
      const group = members.filter((m) => m.timeframe === timeframe);
      const assigned = assign(group);
      const { byDimension, base } = assigned;
      unswept.push(...assigned.unswept);
      for (const [dimension, points] of byDimension) {
        if (!dimensions.includes(dimension)) dimensions.push(dimension);
        const ordered = inParameterOrder(
          points.map((candidate) => ({
            candidate,
            value: candidate.parameters[dimension],
            isDefault: base !== null && candidate === base,
            supported: false,
          })),
        );
        // `supported` is positional, so it can only be decided once the run is in parameter order.
        const states = ordered.map((point) => point.candidate.significance);
        for (const [i, point] of ordered.entries()) {
          point.supported =
            states[i] === PASS && (states[i - 1] === PASS || states[i + 1] === PASS);
        }
        runs.push({ dimension, timeframe, points: ordered, reading: readingOf(states) });
      }
    }

    const passes = members.filter((m) => m.significance === PASS);
    const supported = new Set<number>();
    for (const run of runs) {
      for (const point of run.points) {
        if (point.supported) supported.add(point.candidate.deployment_id);
      }
    }
    groups.push({
      family,
      candidates: members.length,
      timeframes,
      dimensions,
      runs,
      neighbourhoods: runs.filter((run) => run.reading === 'neighbourhood').length,
      supported: supported.size,
      // ⚠ A pass counted once per candidate, not once per appearance. The base configuration is a
      // point on every dimension of its family, so counting appearances would report one promotable
      // default as four promotable candidates — an inflation on the screen whose job is to resist
      // exactly that.
      isolated: passes.filter((m) => !supported.has(m.deployment_id)).length,
      flagged: members.filter((m) => m.significance === 'flagged').length,
      best: bestOf(members),
      unswept,
    });
  }
  return groups;
}

/** The run of a family's dimension on each timeframe, in the family's timeframe order. */
export function runsFor(group: FamilyGroup, dimension: string): Array<DimensionRun | null> {
  return group.timeframes.map(
    (timeframe) =>
      group.runs.find((run) => run.dimension === dimension && run.timeframe === timeframe) ?? null,
  );
}
